import { GameStorageManager, Inject, ModelEvents, Singleton } from '@denishussar/html5-game-engine';
import { GameModel } from '../Models/GameModel';

/**
 * Represents the logic of general and unlimited lives
 */
@Singleton
export class LivesLogic {


    private _passedTimeFromLastUpdate: number = 0;
    private _timerState = true;

    constructor(
        @Inject private _model: GameModel,
        @Inject private _modelEvents: ModelEvents,
        @Inject private _storageManager: GameStorageManager,
    ) {

    }

    /**
     * updates the timer
     * @param {number} delta - delta between the frames, s
     */
    update(delta) {
        this._passedTimeFromLastUpdate += delta
        if (this._passedTimeFromLastUpdate >= 0.25) {
            this._passedTimeFromLastUpdate = 0;
            this.updateLivesTime()
        }
    }

    /**
     * updates model
     */
    public updateLivesTime() {
        /**
         * unlimited lives case
         */
        if (this._model.userBalanceLivesUnlimitedTimestamp > 0) {
            const currentTime = new Date().getTime();
            this._model.userBalanceLivesUnlimitedTime = currentTime - this._model.userBalanceLivesUnlimitedTimestamp;
            if (this._model.userBalanceLivesUnlimitedTime >= this._model.userBalanceLivesUnlimitedPeriod) {
                this.setUnlimitedLives(0)
            }
        }

        /**
         * default lives case
         */
        if (!this._timerState) return;
        if (this._model.userBalanceLivesCount === this._model.userBalanceLivesAvailable){
            this._timerState = false;
            this._model.userBalanceLivesTime = 0;
        }
        if (this._model.userBalanceLivesCount >= this._model.userBalanceLivesAvailable) return;

        const currentTime = new Date().getTime();
        this._model.userBalanceLivesTime = currentTime - this._model.userBalanceLivesTimestamp;
        if (this._model.userBalanceLivesTime >= this._model.userBalanceLivesAddPeriod) {
            this.setLivesCount(this._model.userBalanceLivesCount + 1);
        }
    }

    /**
     * restores data from the storage and checks lives
     */
    public getLivesFromStorage() {
        const livesCount = this._storageManager.loadData('livesCount')
        const livesTimestamp = this._storageManager.loadData('livesTimestamp')
        this._model.userBalanceLivesUnlimitedTimestamp = +this._storageManager.loadData('livesUnlimitedTimestamp');
        this._model.userBalanceLivesUnlimitedPeriod = +this._storageManager.loadData('livesUnlimitedPeriod');

        /**
         * if no data then sets max lives number
         */
        if (livesCount === null) {
            this._model.userBalanceLivesCount = this._model.userBalanceLivesAvailable;
            this._model.userBalanceLivesTimestamp = new Date().getTime();
            return;
        }

        /**
         * if number of lives is bigger than available number then sets max lives number
         */
        if (+livesCount >= this._model.userBalanceLivesAvailable) {
            this._model.userBalanceLivesCount = +livesCount;
            this._model.userBalanceLivesTimestamp = new Date().getTime();
            return;
        }

        /**
         * if number of lives is less than available number then calculates accumulated lives
         */
        if (+livesCount < this._model.userBalanceLivesAvailable) {
            const timePassed = new Date().getTime() - +livesTimestamp
            const addLives = Math.floor(timePassed / this._model.userBalanceLivesAddPeriod)
            if (addLives > this._model.userBalanceLivesAvailable - +livesCount) {
                this._model.userBalanceLivesCount = this._model.userBalanceLivesAvailable;
                this._model.userBalanceLivesTimestamp = new Date().getTime();
            } else {
                this._model.userBalanceLivesCount = +livesCount + addLives;
                const timestamp = +livesTimestamp + addLives * this._model.userBalanceLivesAddPeriod;
                this._model.userBalanceLivesTimestamp = timestamp;
            }
        }
    }

    /**
     * sets unlimited attempts during period
     * @param {} period
     */
    public setUnlimitedLives(period:number) {
        this._model.userBalanceLivesUnlimitedTimestamp = period > 0 ? new Date().getTime() : 0;
        this._model.userBalanceLivesUnlimitedPeriod = period;
        this._model.userBalanceLivesUnlimitedTime = 0;
        this._storageManager.saveData('livesUnlimitedTimestamp', `${this._model.userBalanceLivesUnlimitedTimestamp}`)
        this._storageManager.saveData('livesUnlimitedPeriod', `${this._model.userBalanceLivesUnlimitedPeriod}`)
    }

    /**
     * sets the lives model's properties and save to the storage
     */
    public setLivesCount(number) {
        if (number >= this._model.userBalanceLivesAvailable) {
            this._timerState = false;
            this._model.userBalanceLivesCount = number;
            this.resetLivesTimer()
            this.writeLivesToStorage()
            return;
        }

        if (!this._timerState && number < this._model.userBalanceLivesAvailable) {
            this._model.userBalanceLivesCount = number;
            this.resetLivesTimer()
            this._timerState = true;
            this.writeLivesToStorage();
            return;
        }

        if (this._timerState && number > this._model.userBalanceLivesCount) {
            this.resetLivesTimer()
            this._timerState = true;
            this._model.userBalanceLivesCount = number;
            this.writeLivesToStorage();
            return;
        }

        if (this._timerState && number < this._model.userBalanceLivesCount) {
            this._timerState = true;
            this._model.userBalanceLivesCount = number;
            this.writeLivesToStorage();
            return;
        }
    }

    private writeLivesToStorage() {
        this._storageManager.saveData('livesTimestamp', `${ this._model.userBalanceLivesTimestamp }`);
        this._storageManager.saveData('livesCount', `${ this._model.userBalanceLivesCount }`);
    }

    private resetLivesTimer() {
        this._model.userBalanceLivesTimestamp = new Date().getTime();
        this._model.userBalanceLivesTime = 0;
    }
}
